import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue } from "framer-motion";
import { ArrowRight, Truck, Gift, Award, Instagram, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { FestiveBanner } from "@/components/FestiveBanner";
import { HeroStage3D, ProductRing3D, CocoaParticles, ChocolateWave } from "@/components/ChocolateFX";

export default function Home() {
  const { config } = useStore();
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true&limit=12").then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/collections").then((r) => setCollections(r.data)).catch(() => {});
  }, []);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const onHeroMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section onMouseMove={onHeroMove} className="relative min-h-[88vh] flex items-center overflow-hidden bg-[radial-gradient(ellipse_at_70%_40%,#3B251D_0%,#120A07_65%)]">
        <CocoaParticles count={18} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid lg:grid-cols-[1fr_1.15fr] gap-10 items-center py-14">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
            <span className="eyebrow">Master Chocolatier Confections</span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mt-4">
              The Valley of <span className="gold-gradient-text">Chocolates</span>
            </h1>
            <p className="text-[#C8B8AB] text-lg mt-6 leading-relaxed max-w-xl">
              {config.hero_subtitle || "Fine chocolate and bespoke gifting, crafted for moments worth savouring."}
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-gold px-8 py-4 rounded-full flex items-center gap-2">
                Explore the Collection <ArrowRight size={18} />
              </Link>
              <Link to="/catalogue" data-testid="hero-catalogue-btn" className="btn-outline-gold px-8 py-4 rounded-full flex items-center gap-2">
                <Download size={17} /> Download Catalogue
              </Link>
            </div>
            <div className="flex gap-8 mt-10 text-sm">
              {[["60", "Signature creations"], ["8", "Collections"], ["₹3,500+", "Free delivery"]].map(([n, l]) => (
                <div key={l}><p className="font-display text-2xl gold-gradient-text">{n}</p><p className="text-[#8C7B6E] text-xs mt-1">{l}</p></div>
              ))}
            </div>
          </motion.div>
          <HeroStage3D mx={mx} my={my} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-[4]"><ChocolateWave /></div>
      </section>

      <FestiveBanner />

      {/* MARQUEE */}
      <div className="bg-[#120A07] border-y border-[#3B251D] py-4 overflow-hidden">
        <div className="marquee">
          {Array(2).fill(0).map((_, i) => (
            <span key={i} className="font-display text-2xl text-[#8C7B6E] mx-8">
              Velvet Silk Ganache <span className="text-[#D4AF37] mx-6">✦</span> Single-Origin Reserve <span className="text-[#D4AF37] mx-6">✦</span> Bespoke Cacao Creations <span className="text-[#D4AF37] mx-6">✦</span> Festive Gifting <span className="text-[#D4AF37] mx-6">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {[
          { icon: Truck, t: "Complimentary Delivery", d: "On all orders ₹3,500 and above across India." },
          { icon: Award, t: "Signature Craft", d: "Sixty creations, each finished by hand to a velvet shine." },
          { icon: Gift, t: "Bespoke Gifting", d: "Custom corporate, bulk & festival hampers." },
        ].map((v, i) => (
          <motion.div key={v.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="lux-card p-7 flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] shrink-0"><v.icon size={22} /></div>
            <div><h3 className="font-display text-lg">{v.t}</h3><p className="text-[#8C7B6E] text-sm mt-1">{v.d}</p></div>
          </motion.div>
        ))}
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="eyebrow">Signature Selection</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Most Coveted Creations</h2>
          </div>
          <Link to="/shop" className="text-[#D4AF37] hover:text-[#F3D8A2] text-sm flex items-center gap-1">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7">
          {featured.slice(0, 8).map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
        </div>
      </section>

      {/* 3D RING */}
      <section className="relative py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 text-center mb-4">
          <span className="eyebrow">In Three Dimensions</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">The Signature Carousel</h2>
          <p className="text-[#8C7B6E] text-sm mt-2">Hover to pause · tap any box to open it</p>
        </div>
        {featured.length > 0 && <ProductRing3D items={featured} />}
      </section>

      {/* CATALOGUE CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="lux-card relative overflow-hidden grid md:grid-cols-2 items-center">
          <img src="/catalogue/decor/cocoa-pods.webp" alt="SCHOKODALE catalogue" className="h-full w-full object-cover min-h-[240px]" />
          <div className="p-8 sm:p-12">
            <span className="eyebrow">The Complete Catalogue</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">All 60 creations, in one edition</h2>
            <p className="text-[#C8B8AB] mt-3 text-sm">Browse every truffle, bonbon, festive signature and snacking favourite with exact pack sizes and MRP — download the 24-page catalogue.</p>
            <Link to="/catalogue" data-testid="home-catalogue-cta" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full mt-6"><Download size={17} /> Get the Catalogue</Link>
          </div>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        <div className="text-center mb-12">
          <span className="eyebrow">Seasonal & Festival</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">Curated Collections</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {collections.map((c, i) => (
            <Link key={c.name} to={`/shop?collection=${encodeURIComponent(c.name)}`} data-testid={`collection-${i}`}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden group lux-card">
              <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#120A07] via-[#120A07]/40 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="font-display text-xl text-[#FDFBF7]">{c.name}</h3>
                <p className="text-[#C8B8AB] text-xs mt-1 line-clamp-2">{c.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        <div className="text-center mb-10">
          <span className="eyebrow">@{config.instagram_handle || "schokodale"}</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 flex items-center justify-center gap-3"><Instagram className="text-[#D4AF37]" /> Follow the Craft</h2>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {(config.instagram_feed || []).map((src, i) => (
            <a key={src} href={config.instagram_url} target="_blank" rel="noreferrer" className="aspect-square rounded-xl overflow-hidden group" data-testid={`insta-${i}`}>
              <img src={src} alt="" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
